import { useState } from 'react';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaFacebookF, FaTwitter, FaInstagram, FaChevronDown } from 'react-icons/fa';
import { Navigation, Footer } from '@/types';

interface NavBarProps {
  navigation:Navigation;
  footer:Footer;
  app_name: string;
}

export default function NavBar({
  navigation,
  footer,
  app_name,
}: NavBarProps) {
  const [isOpen, setIsOpen] = useState(false);

  // console.log("navigation", navigation);
  // console.log("footer", footer);
  // console.log("app_name", app_name);

  const { categories, destinations } = navigation;
  const { contact, social_media } = footer;

  return (
    <nav className="sticky top-0 z-50">
      {/* Top Bar */}
      <div className="bg-[#002147] text-white py-2">
        <div className="container max-w-7xl mx-auto px-4 flex flex-wrap justify-between items-center">
          <div className="flex flex-wrap gap-4 text-sm">
            <a href={`mailto:${contact.email}`} className="flex items-center gap-2 hover:text-primary transition-colors">
              <FaEnvelope /> {contact.email}
            </a>
            <a href={`tel:${contact.phone}`} className="flex items-center gap-2 hover:text-primary transition-colors">
              <FaPhone /> {contact.phone}
            </a>
            <span className="flex items-center gap-2">
              <FaMapMarkerAlt /> {contact.address}
            </span>
          </div>
          <div className="flex gap-4">
            <a href={social_media.facebook} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
              <FaFacebookF />
            </a>
            <a href={social_media.twitter} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
              <FaTwitter />
            </a>
            <a href={social_media.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
              <FaInstagram />
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="bg-white shadow-md">
        <div className="container mx-auto px-4 max-w-7xl mx-auto">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <a href="/" className="text-2xl font-bold text-gray-800">
              <img src="/static/logo.png" alt="logo" className="h-16" />
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="/" className="text-gray-800 hover:text-primary transition-colors">
                Home
              </a>

              {/* Tours Dropdown */}
              <div className="relative group">
                <button className="text-gray-800 group-hover:text-primary transition-colors flex items-center gap-1">
                  Tours
                  <FaChevronDown className="w-3 h-3 transform group-hover:rotate-180 transition-transform duration-200" />
                </button>
                <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                  {categories.map((category) => (
                    <a
                      key={category.id}
                      href={`/tours/category/${category.slug}`}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      {category.name}
                    </a>
                  ))}
                </div>
              </div>

              {/* Destinations Dropdown */}
              <div className="relative group">
                <button className="text-gray-800 group-hover:text-primary transition-colors flex items-center gap-1">
                  Destinations
                  {destinations.length > 0 && (
                    <span className="text-gray-500 text-xs">
                    </span>
                  )}
                  <FaChevronDown className="w-3 h-3 transform group-hover:rotate-180 transition-transform duration-200" />
                </button>
                <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                  {destinations.map((destination) => (
                    <div key={destination.id}>
                      <a
                        href={`/destinations/${destination.slug}`}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
                      >
                        <span>{destination.name}</span>
                        {destination.subdestinations.length > 0 && (
                          <span className="text-gray-500 text-xs flex items-center gap-1">
                            ({destination.subdestinations.length})
                            <FaChevronDown className="w-2 h-2 transform -rotate-90" />
                          </span>
                        )}
                      </a>
                      {destination.subdestinations.map((sub) => (
                        <a
                          key={sub.id}
                          href={`/destinations/${sub.slug}`}
                          className="block px-8 py-2 text-sm text-gray-600 hover:bg-gray-100"
                        >
                          {sub.name}
                        </a>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {/* About Dropdown */}
              <div className="relative group">
                <button className="text-gray-800 group-hover:text-primary transition-colors flex items-center gap-1">
                  About
                  <FaChevronDown className="w-3 h-3 transform group-hover:rotate-180 transition-transform duration-200" />
                </button>
                <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                  <a
                    href="/about"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Who We Are
                  </a>
                  <a
                    href="/home/our-services"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Our Services
                  </a>
                  <a
                    href="/home/equipments"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Equipments
                  </a>
                </div>
              </div>

              <a href="/gallery" className="text-gray-800 hover:text-primary transition-colors">
                Gallery
              </a>

              <a href="/contact" className="text-gray-800 hover:text-primary transition-colors">
                Contact Us
              </a>
              <a href="/home/equipments" className="text-gray-800 hover:text-primary transition-colors">
                Equipments
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden rounded-lg p-2 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={isOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
                />
              </svg>
            </button>
          </div>

          {/* Mobile Navigation */}
          {isOpen && (
            <div className="md:hidden pb-4">
              <a
                href="/"
                className="block py-2 text-gray-800 hover:text-primary transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Home
              </a>
              
              {/* Tours */}
              <div className="space-y-2">
                <div className="font-medium text-gray-800 py-2 flex items-center gap-1">
                  Tours
                  <FaChevronDown className="w-3 h-3" />
                </div>
                {categories.map((category) => (
                  <a
                    key={category.id}
                    href={`/tours/category/${category.slug}`}
                    className="block py-2 pl-4 text-gray-700 hover:text-primary transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    {category.name}
                  </a>
                ))}
              </div>

              {/* Destinations */}
              <div className="space-y-2">
                <div className="font-medium text-gray-800 py-2 flex items-center gap-1">
                  Destinations
                  <FaChevronDown className="w-3 h-3" />
                </div>
                {destinations.map((destination) => (
                  <div key={destination.id}>
                    <a
                      href={`/destinations/${destination.slug}`}
                      className="block py-2 pl-4 text-gray-700 hover:text-primary transition-colors flex items-center justify-between pr-4"
                      onClick={() => setIsOpen(false)}
                    >
                      <span>{destination.name}</span>
                      {destination.subdestinations.length > 0 && (
                        <FaChevronDown className="w-3 h-3 transform -rotate-90" />
                      )}
                    </a>
                    {destination.subdestinations.map((sub) => (
                      <a
                        key={sub.id}
                        href={`/destinations/${sub.slug}`}
                        className="block py-2 pl-8 text-gray-600 hover:text-primary transition-colors"
                        onClick={() => setIsOpen(false)}
                      >
                        {sub.name}
                      </a>
                    ))}
                  </div>
                ))}
              </div>

              {/* About Dropdown - Mobile */}
              <div className="space-y-2">
                <div className="font-medium text-gray-800 py-2 flex items-center gap-1">
                  About
                  <FaChevronDown className="w-3 h-3" />
                </div>
                <a
                  href="/about"
                  className="block py-2 pl-4 text-gray-700 hover:text-primary transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  Who We Are
                </a>
                <a
                  href="/home/our-services"
                  className="block py-2 pl-4 text-gray-700 hover:text-primary transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  Our Services
                </a>
                <a
                  href="/home/equipments"
                  className="block py-2 pl-4 text-gray-700 hover:text-primary transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  Equipments
                </a>
              </div>

              <a
                href="/gallery"
                className="block py-2 text-gray-800 hover:text-primary transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Gallery
              </a>

              <a
                href="/contact"
                className="block py-2 text-gray-800 hover:text-primary transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Contact Us
              </a>
              <a
                href="/home/equipments"
                className="block py-2 text-gray-800 hover:text-primary transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Equipments
              </a>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
} 